import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { auth, tts } from '../api/client'

// Markdown components for AI messages
const markdownComponents = {
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="ml-2">{children}</li>,
}

export default function VoiceChatPage() {
  const navigate = useNavigate()
  const [messages, setMessages] = useState([])
  const [isConnected, setIsConnected] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [streamingContent, setStreamingContent] = useState('')
  const [currentConversationId, setCurrentConversationId] = useState(null)

  // Voice state
  const [isListening, setIsListening] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [ttsMode, setTtsMode] = useState('elevenlabs') // 'browser' or 'elevenlabs'
  const [autoSpeak, setAutoSpeak] = useState(true)
  const [speechSupported, setSpeechSupported] = useState(true)

  // Refs
  const wsRef = useRef(null)
  const messagesEndRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const recognitionRef = useRef(null)
  const audioRef = useRef(null)
  const pendingSpeechRef = useRef(null)
  const streamingContentRef = useRef('')

  // Check browser support for Web Speech API
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) {
      setSpeechSupported(false)
    }
  }, [])

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) return

    const recognition = new SpeechRecognition()
    recognition.continuous = false
    recognition.interimResults = true
    recognition.lang = 'en-US'

    recognition.onresult = (event) => {
      let interimTranscript = ''
      let finalTranscript = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result.isFinal) {
          finalTranscript += result[0].transcript
        } else {
          interimTranscript += result[0].transcript
        }
      }

      setTranscript(finalTranscript || interimTranscript)

      if (finalTranscript) {
        // Send the final transcript as a message
        sendMessage(finalTranscript)
        setTranscript('')
      }
    }

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error)
      setIsListening(false)
    }

    recognition.onend = () => {
      setIsListening(false)
    }

    recognitionRef.current = recognition

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort()
      }
    }
  }, [])

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingContent])

  // Speak new AI messages
  useEffect(() => {
    if (!autoSpeak) return

    // Check for new AI message
    const lastMessage = messages[messages.length - 1]
    if (lastMessage && lastMessage.role === 'assistant' && lastMessage.isNew) {
      speakText(lastMessage.content)
      // Mark as not new to prevent re-speaking
      setMessages(prev => prev.map((msg, idx) =>
        idx === prev.length - 1 ? { ...msg, isNew: false } : msg
      ))
    }
  }, [messages, autoSpeak, ttsMode])

  // WebSocket connection
  const connectWebSocket = useCallback(async () => {
    try {
      const chatToken = await auth.getChatToken()
      if (!chatToken) {
        console.error('Failed to get chat token')
        return
      }

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const host = window.location.host
      const wsUrl = `${protocol}//${host}/trainer-chat?token=${chatToken}`

      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        setIsConnected(true)
        console.log('Voice WebSocket connected')
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)

          switch (data.type) {
            case 'message':
            case 'response':
              setIsTyping(false)
              setStreamingContent('')
              setMessages(prev => [...prev, {
                id: data.id || Date.now(),
                content: data.content || data.message,
                role: data.role || 'assistant',
                timestamp: data.timestamp || new Date().toISOString(),
                isNew: true,
              }])
              break

            case 'typing':
            case 'status':
              if (data.status === 'typing' || data.type === 'typing') {
                setIsTyping(true)
              }
              break

            case 'stream':
              setIsTyping(false)
              streamingContentRef.current += data.content
              setStreamingContent(streamingContentRef.current)
              break

            case 'stream_end':
              const finalContent = streamingContentRef.current + (data.content || '')
              if (finalContent) {
                setMessages(prev => [...prev, {
                  id: data.id || Date.now(),
                  content: finalContent,
                  role: 'assistant',
                  timestamp: new Date().toISOString(),
                  isNew: true,
                }])
                streamingContentRef.current = ''
                setStreamingContent('')
              }
              break

            case 'error':
              console.error('WebSocket error:', data.message)
              setIsTyping(false)
              break

            case 'conversation_id':
              setCurrentConversationId(data.conversationId)
              break
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error)
        }
      }

      ws.onclose = () => {
        setIsConnected(false)
        console.log('Voice WebSocket disconnected')
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket()
        }, 3000)
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
      }
    } catch (error) {
      console.error('Failed to connect WebSocket:', error)
    }
  }, [])

  // Connect on mount
  useEffect(() => {
    connectWebSocket()

    return () => {
      if (wsRef.current) {
        wsRef.current.close()
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      // Stop any audio playback
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      // Cancel speech synthesis
      window.speechSynthesis?.cancel()
    }
  }, [connectWebSocket])

  function sendMessage(content) {
    if (!content.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return
    }

    const userMessage = {
      id: Date.now(),
      content: content.trim(),
      role: 'user',
      timestamp: new Date().toISOString(),
    }
    setMessages(prev => [...prev, userMessage])
    setIsTyping(true)
    streamingContentRef.current = ''
    setStreamingContent('')

    wsRef.current.send(JSON.stringify({
      type: 'message',
      content: content.trim(),
      conversationId: currentConversationId,
    }))
  }

  function toggleListening() {
    if (!recognitionRef.current) return

    if (isListening) {
      recognitionRef.current.stop()
      setIsListening(false)
    } else {
      // Stop any ongoing speech
      stopSpeaking()
      setTranscript('')
      recognitionRef.current.start()
      setIsListening(true)
    }
  }

  async function speakText(text) {
    if (!text) return

    // Stop any previous speech
    stopSpeaking()

    setIsSpeaking(true)

    if (ttsMode === 'browser') {
      // Use browser's SpeechSynthesis
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.rate = 1.0
      utterance.pitch = 1.0
      utterance.volume = 1.0

      utterance.onend = () => {
        setIsSpeaking(false)
      }

      utterance.onerror = () => {
        setIsSpeaking(false)
      }

      window.speechSynthesis.speak(utterance)
    } else {
      // Use ElevenLabs TTS
      try {
        const response = await tts.elevenlabs(text)
        if (response.success && response.audio) {
          const audioData = `data:${response.mimeType};base64,${response.audio}`
          const audio = new Audio(audioData)
          audioRef.current = audio

          audio.onended = () => {
            setIsSpeaking(false)
            audioRef.current = null
          }

          audio.onerror = () => {
            setIsSpeaking(false)
            audioRef.current = null
          }

          await audio.play()
        } else {
          console.error('TTS error:', response.error)
          setIsSpeaking(false)
        }
      } catch (error) {
        console.error('TTS request failed:', error)
        setIsSpeaking(false)
      }
    }
  }

  function stopSpeaking() {
    setIsSpeaking(false)

    // Stop browser speech
    window.speechSynthesis?.cancel()

    // Stop audio playback
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      audioRef.current = null
    }
  }

  // Re-speak the last AI message
  function replayLastMessage() {
    const lastAiMessage = [...messages].reverse().find(m => m.role === 'assistant')
    if (lastAiMessage) {
      speakText(lastAiMessage.content)
    }
  }

  return (
    <div className="flex flex-col bg-gray-900" style={{ height: '100dvh' }}>
      {/* Header */}
      <div className="bg-gray-800 safe-area-inset-top">
        <div className="px-4 py-3 flex items-center">
          <button
            onClick={() => navigate('/')}
            className="p-2 -ml-2 text-gray-400 hover:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="ml-2 flex-1">
            <h1 className="font-semibold text-white">Voice Chat</h1>
            <p className="text-xs text-gray-400">
              {isConnected ? 'Connected' : 'Connecting...'}
            </p>
          </div>
          <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-500'}`}></div>
        </div>

        {/* TTS Mode Selector */}
        <div className="px-4 pb-3 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">TTS:</span>
            <button
              onClick={() => setTtsMode('browser')}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                ttsMode === 'browser'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Browser
            </button>
            <button
              onClick={() => setTtsMode('elevenlabs')}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                ttsMode === 'elevenlabs'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              ElevenLabs
            </button>
          </div>
          <button
            onClick={() => setAutoSpeak(!autoSpeak)}
            className={`px-3 py-1 text-xs rounded-full transition-colors ${
              autoSpeak
                ? 'bg-green-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Auto-speak {autoSpeak ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {!speechSupported && (
          <div className="bg-yellow-900/50 text-yellow-200 rounded-lg px-4 py-3 mb-4 text-sm">
            Speech recognition is not supported in this browser. Try Chrome or Safari.
          </div>
        )}

        {messages.length === 0 && !isTyping && (
          <div className="text-center text-gray-400 mt-12">
            <div className="w-24 h-24 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            </div>
            <p className="font-medium text-lg text-white">Voice Chat</p>
            <p className="text-sm mt-2 max-w-xs mx-auto">
              Tap the microphone to start speaking. I'll respond with voice.
            </p>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`mb-4 ${message.role === 'user' ? 'text-right' : 'text-left'}`}
          >
            <div
              className={`inline-block max-w-[85%] px-4 py-3 rounded-2xl ${
                message.role === 'user'
                  ? 'bg-primary-600 text-white rounded-br-md'
                  : 'bg-gray-700 text-gray-100 rounded-bl-md'
              }`}
            >
              <div className="text-sm prose prose-sm prose-invert max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {message.content}
                </ReactMarkdown>
              </div>
            </div>
            {message.role === 'assistant' && (
              <button
                onClick={() => speakText(message.content)}
                className="mt-1 p-1 text-gray-500 hover:text-gray-300"
                title="Replay"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
              </button>
            )}
          </div>
        ))}

        {/* Streaming message */}
        {streamingContent && (
          <div className="mb-4 text-left">
            <div className="inline-block max-w-[85%] px-4 py-3 rounded-2xl bg-gray-700 text-gray-100 rounded-bl-md">
              <div className="text-sm prose prose-sm prose-invert max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {streamingContent}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        )}

        {/* Typing indicator */}
        {isTyping && (
          <div className="mb-4 text-left">
            <div className="inline-block px-4 py-3 rounded-2xl bg-gray-700 rounded-bl-md">
              <div className="flex items-center space-x-1.5">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Transcript preview */}
      {transcript && (
        <div className="bg-gray-800 px-4 py-2 border-t border-gray-700">
          <p className="text-gray-300 text-sm italic">{transcript}</p>
        </div>
      )}

      {/* Voice Controls */}
      <div className="bg-gray-800 border-t border-gray-700 px-4 py-6">
        <div className="flex items-center justify-center gap-6">
          {/* Stop Speaking Button */}
          {isSpeaking && (
            <button
              onClick={stopSpeaking}
              className="p-4 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
              title="Stop speaking"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            </button>
          )}

          {/* Main Microphone Button */}
          <button
            onClick={toggleListening}
            disabled={!speechSupported || !isConnected}
            className={`p-6 rounded-full transition-all transform ${
              isListening
                ? 'bg-red-500 scale-110 animate-pulse'
                : 'bg-primary-600 hover:bg-primary-700 hover:scale-105'
            } text-white disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={isListening
                  ? "M21 12a9 9 0 11-18 0 9 9 0 0118 0z M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"
                  : "M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                }
              />
            </svg>
          </button>

          {/* Replay Button */}
          {messages.some(m => m.role === 'assistant') && !isSpeaking && (
            <button
              onClick={replayLastMessage}
              className="p-4 bg-gray-700 text-gray-300 rounded-full hover:bg-gray-600 transition-colors"
              title="Replay last response"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}
        </div>

        {/* Status text */}
        <p className="text-center text-gray-400 text-sm mt-4">
          {!isConnected ? 'Connecting...' : isListening ? 'Listening...' : isSpeaking ? 'Speaking...' : 'Tap microphone to speak'}
        </p>

        {/* Retry button if disconnected */}
        {!isConnected && (
          <button
            onClick={connectWebSocket}
            className="mt-3 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 transition-colors"
          >
            Retry Connection
          </button>
        )}

        {/* Text input fallback */}
        <form onSubmit={(e) => { e.preventDefault(); const input = e.target.elements.voiceText; if (input.value.trim()) { sendMessage(input.value.trim()); input.value = ''; } }} className="mt-4 flex gap-2 w-full max-w-md mx-auto">
          <input
            name="voiceText"
            type="text"
            placeholder={isConnected ? "Or type a message..." : "Connecting..."}
            disabled={!isConnected}
            className="flex-1 bg-gray-800 text-white border border-gray-600 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-primary-500 disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!isConnected}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  )
}
